import { z } from "zod";
import { DateOnlySchema, IdSchema, IsoDateTimeSchema } from "../common/primitives.js";
import { PlanCodeSchema, SubscriptionStatusSchema } from "./index.js";
const BillingCycleSchema = z.enum(["monthly", "annual"]);
const STORAGE_ADDON_CODES = ["storage_100gb", "storage_1tb"];
const StorageAddonsSchema = z.strictObject({
  storage_100gb: z.int().min(0).max(50).default(0),
  storage_1tb: z.int().min(0).max(20).default(0)
});
const MinorSchema = z.string().regex(/^-?(0|[1-9]\d{0,17})$/);
const SubscriptionShapeSchema = z.strictObject({
  planCode: PlanCodeSchema,
  cycle: BillingCycleSchema,
  extraSeats: z.int().min(0).max(500),
  addons: StorageAddonsSchema
});
const SubscriptionUsageSchema = z.strictObject({
  seats: z.strictObject({ used: z.int().min(0), limit: z.int().min(0).nullable() }),
  storageBytes: z.strictObject({ used: MinorSchema, limit: MinorSchema.nullable() }),
  hostedSpaces: z.strictObject({ used: z.int().min(0), limit: z.int().min(0).nullable() })
});
const PENDING_CHANGE_KINDS = ["scheduled", "pause", "cancel"];
const SubscriptionSchema = z.strictObject({
  status: SubscriptionStatusSchema,
  shape: SubscriptionShapeSchema,
  trialEndsAt: IsoDateTimeSchema.nullable(),
  currentPeriod: z.strictObject({ start: IsoDateTimeSchema, end: IsoDateTimeSchema }),
  /** When payment is due (trial end, period end): the dunning stages count from it. */
  dueAt: IsoDateTimeSchema.nullable(),
  /** HT price of one period of the current shape (null when on quote). */
  periodPriceMinor: MinorSchema.nullable(),
  /** A change waiting for the end of the period (downgrade, cycle change, pause, cancellation). */
  pendingChange: z.strictObject({
    kind: z.enum(PENDING_CHANGE_KINDS),
    effectiveAt: IsoDateTimeSchema,
    shape: SubscriptionShapeSchema.nullable(),
    months: z.int().min(1).max(3).nullable()
  }).nullable(),
  pausedUntil: IsoDateTimeSchema.nullable(),
  /** Months of pause taken this calendar year (at most 3). */
  pausedMonthsThisYear: z.int().min(0),
  cancelAt: IsoDateTimeSchema.nullable(),
  paymentMethod: z.string().nullable(),
  usage: SubscriptionUsageSchema,
  /** The platform invoice waiting to be paid, if any. */
  openInvoice: z.strictObject({
    id: IdSchema,
    number: z.string(),
    totalTtcMinor: MinorSchema,
    purpose: z.string()
  }).nullable(),
  version: z.int().min(1)
});
const SubscriptionChangeRequestSchema = SubscriptionShapeSchema;
const CHANGE_KINDS = ["start", "upgrade", "restart", "scheduled", "none"];
const CHARGE_CODES = [
  "plan",
  "extra_seats",
  "addon",
  "proration",
  "credit_unused",
  "pause",
  "fair_billing"
];
const DOWNGRADE_METRICS = ["seats", "storage", "hosted_spaces"];
const DowngradeBlockerSchema = z.strictObject({
  metric: z.enum(DOWNGRADE_METRICS),
  used: MinorSchema,
  limit: MinorSchema
});
const SubscriptionQuoteSchema = z.strictObject({
  kind: z.enum(CHANGE_KINDS),
  effectiveAt: IsoDateTimeSchema,
  newPeriod: z.strictObject({ start: IsoDateTimeSchema, end: IsoDateTimeSchema }).nullable(),
  days: z.strictObject({ total: z.int().min(1), remaining: z.int().min(0) }).nullable(),
  lines: z.array(
    z.strictObject({
      code: z.enum(CHARGE_CODES),
      addon: z.enum(STORAGE_ADDON_CODES).nullable(),
      quantity: z.int(),
      unitPriceMinor: MinorSchema,
      amountMinor: MinorSchema
    })
  ),
  totalHtMinor: MinorSchema,
  /** With VAT and stamp duty, as the invoice will show it (0 when nothing is billed now). */
  totalTtcMinor: MinorSchema,
  /** Non-empty: the change is refused until these are reduced (a downgrade). */
  blockers: z.array(DowngradeBlockerSchema)
});
const SubscriptionChangeResultSchema = z.strictObject({
  subscription: SubscriptionSchema,
  invoice: z.strictObject({ id: IdSchema, number: z.string(), totalTtcMinor: MinorSchema }).nullable()
});
const SubscriptionPauseRequestSchema = z.strictObject({ months: z.int().min(1).max(3) });
const BILLING_INVOICE_PURPOSES = [
  "start",
  "upgrade",
  "restart",
  "renewal",
  "resume"
];
const BillingInvoiceSchema = z.strictObject({
  id: IdSchema,
  number: z.string(),
  purpose: z.enum(BILLING_INVOICE_PURPOSES),
  issueDate: DateOnlySchema,
  dueDate: DateOnlySchema,
  periodStart: IsoDateTimeSchema.nullable(),
  periodEnd: IsoDateTimeSchema.nullable(),
  totalTtcMinor: MinorSchema,
  status: z.enum(["open", "paid", "void"]),
  paidAt: IsoDateTimeSchema.nullable()
});
const BillingInvoiceListSchema = z.strictObject({
  items: z.array(BillingInvoiceSchema).max(200)
});
const BillingInvoiceParamsSchema = z.strictObject({ orgId: IdSchema, invoiceId: IdSchema });
const BillingInvoicePdfUrlSchema = z.strictObject({
  url: z.url(),
  expiresAt: IsoDateTimeSchema
});
const CreatePaymentLinkRequestSchema = z.strictObject({ invoiceId: IdSchema });
const PaymentLinkSchema = z.strictObject({
  url: z.url(),
  expiresAt: IsoDateTimeSchema,
  amountMinor: MinorSchema
});
const PaymentNotificationSchema = z.strictObject({
  id: z.string().min(8).max(100),
  type: z.enum(["payment.succeeded", "payment.failed", "payment.expired"]),
  linkId: z.string().min(1).max(100),
  amountMinor: MinorSchema,
  currency: z.literal("TND"),
  occurredAt: IsoDateTimeSchema
});
const PaymentProviderParamsSchema = z.strictObject({ provider: z.enum(["mock"]) });
export {
  BILLING_INVOICE_PURPOSES,
  BillingCycleSchema,
  BillingInvoiceListSchema,
  BillingInvoiceParamsSchema,
  BillingInvoicePdfUrlSchema,
  BillingInvoiceSchema,
  CHANGE_KINDS,
  CHARGE_CODES,
  CreatePaymentLinkRequestSchema,
  DOWNGRADE_METRICS,
  DowngradeBlockerSchema,
  PENDING_CHANGE_KINDS,
  PaymentLinkSchema,
  PaymentNotificationSchema,
  PaymentProviderParamsSchema,
  STORAGE_ADDON_CODES,
  StorageAddonsSchema,
  SubscriptionChangeRequestSchema,
  SubscriptionChangeResultSchema,
  SubscriptionPauseRequestSchema,
  SubscriptionQuoteSchema,
  SubscriptionSchema,
  SubscriptionShapeSchema,
  SubscriptionUsageSchema
};
//# sourceMappingURL=subscription.js.map